import React, { useEffect, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { clsx } from 'clsx';
import { BenchmarkRate, RateType } from '@/types';
import type { DayCountConvention } from '@/types';
import { DecimalInput } from '@/components/ui/DecimalInput';
import { isLowRateWarning, LOW_RATE_WARN_PCT } from '@/services/trades/rerate-mapper';
import type { TradeForm, SearchInventoryItem, CalculatedValues } from './types';
import { inputClassName, labelClassName, readOnlyInputClassName, isAmendLocked } from './types';
import {
  REASONS_WITH_COMMENT,
  TRADE_REASON_OPTIONS,
  TradeReasonValue,
  getDefaultBenchmarkByCurrency,
} from '../../hooks/useCreateTradeForm';
import type { TradeFormValues } from '../../hooks/useCreateTradeForm';
import { BENCHMARK_KEYS } from '@/hooks';
import type { BenchmarkMeta } from '@/hooks';
import { dayCountForCurrency } from '@/services/trades/trade-calculations';
import { formatDecimalString } from '@/utils/decimal-display';

const SPREAD_REGEX = /^-?\d*\.?\d{0,2}$/;
const isIntermediateSpreadInput = (v: string) => v === '' || v === '-' || v.endsWith('.');

const formatSpreadBpsForDisplay = (bps: number | undefined | null): string => {
  if (bps === undefined || bps === null) return '';
  return String(bps);
};

export interface SpreadBpsInputInnerProps {
  field: {
    state: { value: number | undefined; meta: { errors: Array<string | undefined> } };
    handleChange: (v: number | undefined) => void;
  };
  selectedInventoryItem: SearchInventoryItem | null;
  isSubmitted: boolean;
  amendMode?: boolean;
}

export const SpreadBpsInputInner: React.FC<SpreadBpsInputInnerProps> = ({ field, selectedInventoryItem, isSubmitted, amendMode }) => {
  const bpsValue = field.state.value;
  // LR-495: CREATE locks until a security is picked; amend locks via the registry.
  const inputsDisabled = !amendMode && !selectedInventoryItem;
  const [inputValue, setInputValue] = useState<string>(() => formatSpreadBpsForDisplay(bpsValue));

  // Sync down from bps when changed externally (reset, prefill, mode switch).
  // Preserve mid-entry states that would be destroyed by a sync ('-', trailing '.').
  // Empty '' is NOT preserved — sync is the only way to show a prefilled/reset bps
  // after an initial empty render.
  useEffect(() => {
    if (inputValue === '-' || inputValue.endsWith('.')) return;
    const localAsNum = parseFloat(inputValue);
    if (!isNaN(localAsNum) && localAsNum === bpsValue) return;
    setInputValue(formatSpreadBpsForDisplay(bpsValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpsValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let valStr = e.target.value;
    if (!(valStr === '' || valStr === '-' || SPREAD_REGEX.test(valStr))) return;

    if (valStr.length > 1 && valStr.startsWith('0') && valStr[1] !== '.') {
      valStr = valStr.replace(/^0+/, '');
      if (valStr === '' || valStr.startsWith('.')) valStr = '0' + valStr;
    }
    if (valStr.length > 2 && valStr.startsWith('-0') && valStr[2] !== '.') {
      valStr = '-' + valStr.substring(2).replace(/^0+/, '');
      if (valStr === '-' || valStr.startsWith('-.')) valStr = '-0' + valStr.substring(1);
    }

    setInputValue(valStr);

    if (isIntermediateSpreadInput(valStr)) return;
    const parsed = parseFloat(valStr);
    if (!isNaN(parsed)) field.handleChange(parsed);
  };

  const handleBlur = () => {
    if (isIntermediateSpreadInput(inputValue)) setInputValue(formatSpreadBpsForDisplay(bpsValue));
  };

  const showError = isSubmitted && field.state.meta.errors.length > 0;
  return (
    <>
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        disabled={inputsDisabled || isAmendLocked(amendMode, 'spread_bps')}
        placeholder="e.g., 50 or -25"
        className={clsx(inputClassName, showError && 'border-red-500')}
      />
      {showError && (
        <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
      )}
    </>
  );
};

interface RateDetailsSectionProps {
  form: TradeForm;
  isSubmitted: boolean;
  selectedInventoryItem: SearchInventoryItem | null;
  rateType: RateType | undefined;
  rateEntryMode: 'rebate' | 'fee' | undefined;
  securityCurrency: string;
  /** LR-459: settlement-currency context so the Daily Earnings field can also show
   *  the converted figure for cross-currency trades. Optional — same-ccy callers
   *  (and unit tests) omit them and the second field stays hidden. */
  isCrossCurrency?: boolean;
  effectiveSettlementCurrency?: string;
  totalRate: number;
  calculatedValues: CalculatedValues;
  /** Benchmark percentages from the backend registry (LR-435). Partial: only
   * offered benchmarks (BENCHMARK_KEYS) are present — access via `?? 0`. */
  benchmarkRates: Partial<Record<BenchmarkRate, number>>;
  /** Per-benchmark currency + Bloomberg ticker for the dropdown (LR-443).
   * Partial: only offered benchmarks present — access via optional chaining. */
  benchmarkMeta: Partial<Record<BenchmarkRate, BenchmarkMeta>>;
  amendMode?: boolean;
}

// LR-459 round 2: currency-aware, crash-safe money formatter — same guarded
// `formatDecimalString` as TradeSummarySection / the blotter / the single-trade
// view, so every money cell in the form shows its currency symbol consistently
// (was a bare number here vs a symbol in TradeSummarySection). Callers pass the
// figure's own currency (security for the rate-engine legs, settlement for the
// converted daily return).
const formatCurrency = (value: number | null | undefined, currency: string): string =>
  formatDecimalString(value, { currency, dp: 2 });

const formatPercent = (value: number): string =>
  `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

export const RateDetailsSection: React.FC<RateDetailsSectionProps> = ({
  form,
  isSubmitted,
  selectedInventoryItem,
  rateType,
  rateEntryMode,
  securityCurrency,
  isCrossCurrency,
  effectiveSettlementCurrency,
  totalRate,
  calculatedValues,
  benchmarkRates,
  benchmarkMeta,
  amendMode,
}) => {
  // p.10b (LR-450): the trade-entry form always labels the rebate rate
  // "Rebate Rate" / "Rebate Spread (BPS)", even for GMRA repo. The global
  // getRateLabel rename across the single-trade view, the blotter column and
  // the backend `_rate_label` is tracked separately in LR-453.
  const rateLabel = 'Rebate Rate';
  const spreadLabel = 'Rebate Spread (BPS)';

  // LR-617: fee is a GMSLA-only mode — hide the option for Repo/Reverse Repo.
  // trade_type is read from form state (the tradeType prop was deliberately
  // removed in LR-450). Exception: amending a legacy fee-booked repo keeps the
  // option, or the disabled select renders blank (value fallback is 'rebate').
  const tradeType = useStore(form.store, (state: { values: TradeFormValues }) => state.values.trade_type);
  const isRepoTrade = tradeType === 'repo' || tradeType === 'reverse_repo';
  const showFeeOption = !isRepoTrade || (!!amendMode && rateEntryMode === 'fee');

  const isFeeMode = rateEntryMode === 'fee';
  const isRebateFixed = rateEntryMode === 'rebate' && rateType === 'fixed';
  const isRebateFloating = rateEntryMode === 'rebate' && rateType === 'floating';
  // LR-631: a pooled fee trade has no benchmark to choose - the cost leg accrues
  // at the POOL's rate ("already embedded in the cash pool"), which the pool
  // itself writes into benchmark_name / benchmark_rate. The control stays for a
  // fee trade with no pool: amending one booked before LR-631 made it mandatory.
  const cashPool = useStore(form.store, (state: { values: TradeFormValues }) => state.values.cash_pool);
  const showBenchmark = isRebateFloating || (isFeeMode && cashPool == null);
  // LR-365: Total rate field hidden for Rebate fixed (duplicate of rebate rate %).
  // For Rebate floating it is renamed to "Rebate rate".
  const showTotalRate = isFeeMode || isRebateFloating;
  const totalRateLabel = isRebateFloating ? 'Rebate Rate' : 'Total Rate';
  // LR-495: securityCurrency already tracks the picked item in create and the
  // booked position currency in amend, so it replaces the direct read off the
  // (now removed in amend) synthetic selectedInventoryItem.
  const currencyCode = securityCurrency;
  // CREATE disables inputs until a security is picked; amend governs editability
  // via the registry (isAmendLocked), no longer via a synthetic item.
  const inputsDisabled = !amendMode && !selectedInventoryItem;

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Rate Entry Mode Selector */}
        <div>
          <form.Field name="rate_entry_mode">
            {(field) => (
              <>
                <label className={labelClassName}>
                  Rate Entry Mode
                </label>
                <select
                  value={field.state.value || 'rebate'}
                  onChange={(e) => field.handleChange(e.target.value as 'rebate' | 'fee')}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'rate_entry_mode')}
                  className={clsx(inputClassName, inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                >
                  <option value="rebate">{rateLabel}</option>
                  {showFeeOption && <option value="fee">Fee</option>}
                </select>
              </>
            )}
          </form.Field>
        </div>

        {/* Rate Type (only for Rebate Mode) */}
        {rateEntryMode === 'rebate' && (
          <div>
            <form.Field
              name="rate_type"
              validators={{
                onChange: ({ value }) => {
                  const entryMode = form.state.values.rate_entry_mode;
                  if (entryMode !== 'rebate') return undefined;
                  if (!value) return 'Rate type is required';
                  return undefined;
                },
              }}
            >
              {(field) => (
                <>
                  <label className={labelClassName}>
                    Rate Type
                  </label>
                  <select
                    value={field.state.value}
                    onChange={(e) => {
                      const newRateType = e.target.value as RateType;
                      field.handleChange(newRateType);
                      // Reset other rate fields
                      form.setFieldValue('benchmark_name', undefined);
                      form.setFieldValue('benchmark_rate', '0.0000');
                      form.setFieldValue('spread_bps', undefined);
                    }}
                    disabled={inputsDisabled || isAmendLocked(amendMode, 'rate_type')}
                    className={clsx(inputClassName, inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                  >
                    <option value="floating">Floating</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </>
              )}
            </form.Field>
          </div>
        )}

        {/* Rebate Rate % (Rebate Mode + Fixed) */}
        {isRebateFixed && (
          <div>
            <form.Field
              name="benchmark_rate"
              validators={{
                onChange: ({ value }) => {
                  const entryMode = form.state.values.rate_entry_mode;
                  const rType = form.state.values.rate_type;
                  if (entryMode !== 'rebate' || rType !== 'fixed') return undefined;
                  // '0', '0.0000', and negative values are legitimate user
                  // input (deep-special trades can have negative or zero
                  // rebate rates). Block only on missing or non-numeric
                  // intermediate states ('-', '.', etc.) — the regex allows
                  // them mid-edit, but they must not pass to submit since
                  // the backend rejects Decimal('-').
                  if (value === undefined || value === null || value === '' || value === '-' || value === '-.') {
                    return 'Rate is required';
                  }
                  const n = parseFloat(value as string);
                  if (isNaN(n)) return 'Rate is required';
                  return undefined;
                },
              }}
            >
              {(field) => {
                const parsedRate = parseFloat(field.state.value as string);
                // Soft typo guard: −10% is well below any plausible benchmark.
                // Non-blocking warning, mirrors the dividend_passthrough < 85
                // pattern below. Threshold shared with both Re-Rate surfaces so
                // booking and re-rating cannot disagree on what looks wrong.
                const hasNegativeWarning = isLowRateWarning(parsedRate);
                return (
                  <>
                    <label className={labelClassName}>
                      {rateLabel} % <span className="text-red-500">*</span>
                    </label>
                    <DecimalInput
                      value={Number.isFinite(parsedRate) ? parsedRate : undefined}
                      onChange={(v) => field.handleChange(v === undefined ? '' : String(v))}
                      precision={4}
                      allowNegative
                      disabled={inputsDisabled || isAmendLocked(amendMode, 'benchmark_rate')}
                      className={clsx(
                        inputClassName,
                        isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                        hasNegativeWarning && field.state.meta.errors.length === 0 && 'border-yellow-500',
                      )}
                    />
                    {isSubmitted && field.state.meta.errors.length > 0 && (
                      <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                    )}
                    {(() => {
                      // p.11 (LR-450): show the current benchmark rate as a
                      // reference instead of the confusing "X% below SOFR" delta.
                      // Mirrors the floating-mode "Current value" hint (LR-443),
                      // but adds the benchmark name (fixed mode has no dropdown).
                      const benchmarkKey = getDefaultBenchmarkByCurrency(securityCurrency);
                      // JPY/HKD resolve to 'other' — no standard overnight
                      // benchmark, the trader sets the rate manually, so there is
                      // nothing to anchor to (see getDefaultBenchmarkByCurrency).
                      if (benchmarkKey === 'other') return null;
                      const benchmarkValue = benchmarkRates[benchmarkKey];
                      if (benchmarkValue === undefined) return null;
                      const bbgTicker = benchmarkMeta[benchmarkKey]?.bbgTicker;
                      return (
                        <p className="mt-1 text-xs text-gray-600">
                          Current {benchmarkKey.toUpperCase()}: {benchmarkValue}%
                          {bbgTicker ? `  ·  ${bbgTicker}` : ''}
                        </p>
                      );
                    })()}
                    {hasNegativeWarning && field.state.meta.errors.length === 0 && (
                      <p className="mt-1 text-xs text-yellow-600">
                        Negative rate below {LOW_RATE_WARN_PCT}% is unusual. Verify before submit.
                      </p>
                    )}
                  </>
                );
              }}
            </form.Field>
          </div>
        )}

        {/* Benchmark Selector (Rebate + Floating, or a pool-less Fee trade) */}
        {showBenchmark && (
          <div>
            <form.Field
              name="benchmark_name"
              validators={{
                onChange: ({ value }) => {
                  const entryMode = form.state.values.rate_entry_mode;
                  const rType = form.state.values.rate_type;
                  // LR-631: a pooled fee trade takes its benchmark from the pool,
                  // so requiring one here would block a form that has no field
                  // to fill it in.
                  const pooledFee = entryMode === 'fee' && form.state.values.cash_pool != null;
                  const isRelevant = !pooledFee
                    && (entryMode === 'fee' || (entryMode === 'rebate' && rType === 'floating'));
                  if (!isRelevant) return undefined;
                  if (!value) return 'Benchmark is required';
                  return undefined;
                },
              }}
            >
              {(field) => (
                <>
                  <label className={labelClassName}>
                    Benchmark
                  </label>
                  <select
                    value={field.state.value || ''}
                    onChange={(e) => {
                      const name = e.target.value as BenchmarkRate;
                      field.handleChange(name);
                      if (name) {
                        form.setFieldValue('benchmark_rate', (benchmarkRates[name] ?? 0).toString());
                      }
                    }}
                    disabled={inputsDisabled || isAmendLocked(amendMode, 'benchmark_name')}
                    className={inputClassName}
                  >
                    <option value="">Select...</option>
                    {BENCHMARK_KEYS.map(name => {
                      const currency = benchmarkMeta[name]?.currency;
                      return (
                        <option key={name} value={name}>
                          {name.toUpperCase()}{currency ? ` — ${currency}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {field.state.value && (() => {
                    const selected = field.state.value as BenchmarkRate;
                    const bbgTicker = benchmarkMeta[selected]?.bbgTicker;
                    return (
                      <p className="mt-1 text-xs text-gray-600">
                        Current value: {benchmarkRates[selected] ?? 0}%
                        {bbgTicker ? `  ·  ${bbgTicker}` : ''}
                      </p>
                    );
                  })()}
                </>
              )}
            </form.Field>
          </div>
        )}

        {/* Fee % (Fee Mode) — placed before Spread so Fee-mode row 1 reads:
            Rate entry mode | Benchmark | Fee | Total rate. */}
        {isFeeMode && (
          <div>
            <form.Field
              name="fee_percent"
              validators={{
                onChange: ({ value }) => {
                  const entryMode = form.state.values.rate_entry_mode;
                  if (entryMode !== 'fee') return undefined;
                  if (!value) return 'Fee is required';
                  if (parseFloat(value) <= 0) return 'Fee must be positive';
                  return undefined;
                },
              }}
            >
              {(field) => {
                const raw = field.state.value;
                const numValue =
                  typeof raw === 'number'
                    ? raw
                    : raw !== undefined && raw !== ''
                      ? parseFloat(raw as string)
                      : undefined;
                return (
                  <>
                    <label className={labelClassName}>
                      Fee % <span className="text-red-500">*</span>
                    </label>
                    <DecimalInput
                      value={Number.isFinite(numValue) ? (numValue as number) : undefined}
                      onChange={(v) => field.handleChange(v === undefined ? '' : String(v))}
                      precision={3}
                      disabled={inputsDisabled || isAmendLocked(amendMode, 'fee_percent')}
                      className={inputClassName}
                    />
                  </>
                );
              }}
            </form.Field>
          </div>
        )}

        {/* Spread BPS (Rebate Mode + Floating) */}
        {isRebateFloating && (
          <div>
            <form.Field
              name="spread_bps"
              validators={{
                onChange: ({ value }) => {
                  const entryMode = form.state.values.rate_entry_mode;
                  const rType = form.state.values.rate_type;
                  if (entryMode !== 'rebate' || rType !== 'floating') return undefined;
                  if (value === undefined) return 'Spread is required';
                  // LR-464: rebate = benchmark + spread/100 may be negative for
                  // special stocks, which carry a negative spread (e.g. ALKAL with
                  // rebate −943 %). No client-side positivity guard — backend mirrors this.
                  return undefined;
                },
              }}
            >
              {(field) => (
                <>
                  <label className={labelClassName}>
                    {spreadLabel} <span className="text-red-500">*</span>
                  </label>
                  <SpreadBpsInputInner
                    field={field as unknown as SpreadBpsInputInnerProps['field']}
                    selectedInventoryItem={selectedInventoryItem}
                    isSubmitted={isSubmitted}
                    amendMode={amendMode}
                  />
                </>
              )}
            </form.Field>
          </div>
        )}

        {/* Total Rate / Rebate Rate display. Hidden for Rebate fixed
            (rate equals rebate rate % above). */}
        {showTotalRate && (
          <div>
            <label className={labelClassName}>
              {totalRateLabel}
            </label>
            <div className={clsx(readOnlyInputClassName, 'text-gray-900 font-semibold')}>
              {formatPercent(totalRate)}
            </div>
          </div>
        )}

        {/* Day-count convention (LR-415). Auto = derive from currency
            (GBP/HKD/JPY → ACT/365, others → ACT/360). Override applies to rate
            engine calcs (daily / annual) and accrual snapshots on the
            position. */}
        <div>
          <form.Field name="day_count_convention">
            {(field) => {
              const value = field.state.value as DayCountConvention | null | undefined;
              const isAuto = value == null;
              const derivedDc = dayCountForCurrency(securityCurrency);
              return (
                <>
                  <label className={labelClassName}>
                    Day Count
                  </label>
                  <select
                    value={value ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.handleChange(v === '' ? null : (v as DayCountConvention));
                    }}
                    disabled={inputsDisabled || isAmendLocked(amendMode, 'day_count_convention')}
                    className={inputClassName}
                  >
                    <option value="">Auto (by currency)</option>
                    <option value="360">ACT/360</option>
                    <option value="365">ACT/365</option>
                  </select>
                  {isAuto && (
                    <p className="mt-1 text-xs text-gray-600">
                      Auto → ACT/{derivedDc}
                      {securityCurrency ? ` (${securityCurrency.toUpperCase()})` : ''}
                    </p>
                  )}
                </>
              );
            }}
          </form.Field>
        </div>

        {/* Dividend Pass-Through */}
        <div>
          <form.Field
            name="dividend_passthrough_percent"
            validators={{
              onChange: ({ value }) => {
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                if (numValue !== undefined) {
                  if (numValue < 0 || numValue > 100) {
                    return 'Dividend pass-through must be between 0 and 100%';
                  }
                }
                return undefined;
              },
            }}
          >
            {(field) => {
              const numValue = typeof field.state.value === 'string' ? parseFloat(field.state.value) : field.state.value;
              const hasWarning = numValue !== undefined && numValue < 85;
              return (
                <>
                  <label className={labelClassName}>
                    Dividend Pass-Through (%)
                  </label>
                  <DecimalInput
                    value={Number.isFinite(numValue) ? (numValue as number) : undefined}
                    onChange={(v) => field.handleChange(v === undefined ? '' : String(v))}
                    precision={0}
                    disabled={inputsDisabled || isAmendLocked(amendMode, 'dividend_passthrough_percent')}
                    placeholder="100"
                    className={clsx(
                      inputClassName,
                      isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                      hasWarning && field.state.meta.errors.length === 0 && 'border-yellow-500',
                      inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                    )}
                  />
                  {isSubmitted && field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                  )}
                  {hasWarning && field.state.meta.errors.length === 0 && (
                    <p className="mt-1 text-xs text-yellow-600">
                      Dividend pass-through below 85% is unusual
                    </p>
                  )}
                </>
              );
            }}
          </form.Field>
        </div>

        {/* Fee-mode daily breakdown: Cash Pool Interest Accrual | Fee Income |
            Net Amount. Backend matches these via @property on Position (LR-346
            percent convention). LR-631 renamed the cost leg after what it now
            is: the interest the collateral earns in its cash pool ("суть одна и
            та же, оно точно так же пойдёт в биллинг", 2026-08-24). */}
        {isFeeMode && (
          <>
            <div>
              <label className={labelClassName}>
                Cash Pool Interest Accrual ({currencyCode})
              </label>
              <input
                type="text"
                value={
                  calculatedValues.costOfCollateral === null
                    ? '—'
                    : formatCurrency(calculatedValues.costOfCollateral ?? 0, currencyCode)
                }
                readOnly
                className={readOnlyInputClassName}
              />
              {calculatedValues.costOfCollateral === null && (
                <p className="mt-1 text-xs text-gray-600">Accrued by the cash pool</p>
              )}
            </div>
            <div>
              <label className={labelClassName}>
                Fee Income ({currencyCode})
              </label>
              <input
                type="text"
                value={formatCurrency(calculatedValues.feeIncome ?? 0, currencyCode)}
                readOnly
                className={readOnlyInputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>
                Net Amount ({currencyCode})
              </label>
              <input
                type="text"
                value={formatCurrency(calculatedValues.netAmount ?? 0, currencyCode)}
                readOnly
                className={readOnlyInputClassName}
              />
            </div>
          </>
        )}

        {/* Trade Reason */}
        <div>
          <form.Field
            name="trade_reason"
            validators={{
              onChange: ({ value }) => {
                if (!value) return 'Trade reason is required';
                return undefined;
              },
            }}
          >
            {(field) => (
              <>
                <label className={labelClassName}>
                  Trade Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'trade_reason')}
                  className={clsx(
                    inputClassName,
                    isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                    inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                  )}
                >
                  <option value="">Select...</option>
                  {TRADE_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {isSubmitted && field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                )}
                {field.state.value && rateType === 'floating' && (() => {
                  const spread = form.state.values.spread_bps;
                  if (spread === undefined || spread === null) return null;
                  const spreadPercent = spread / 100;
                  // LR-464: rate = benchmark + spread. A SPECIAL stock (high
                  // lending fee) sits well BELOW benchmark → a strongly NEGATIVE
                  // spread; GENERAL_COLLATERAL sits near benchmark → a small
                  // spread. Thresholds are the negation of the old (benchmark −
                  // spread) cut-offs, so the nudge behaviour is unchanged.
                  const pct = spreadPercent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero' });
                  if (field.state.value === 'SPECIAL' && spreadPercent > -1.0) {
                    return (
                      <p className="mt-1 text-xs text-yellow-600">
                        Rate spread ({pct}%) is not low enough for a SPECIAL trade. Consider GENERAL_COLLATERAL.
                      </p>
                    );
                  }
                  if (field.state.value === 'GENERAL_COLLATERAL' && spreadPercent < -2.0) {
                    return (
                      <p className="mt-1 text-xs text-yellow-600">
                        Very negative rate spread ({pct}%) suggests this is a SPECIAL trade.
                      </p>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </form.Field>
        </div>

        {/* Daily Earnings — LR-474: settlement-ccy field FIRST for cross-ccy
            trades (settlement-primary doctrine); security-ccy original second.
            Same-ccy renders the single security field. */}
        {isCrossCurrency &&
          effectiveSettlementCurrency &&
          calculatedValues.expectedDailyReturnSettlement !== undefined && (
            <div>
              <label className={labelClassName}>
                Daily Earnings ({effectiveSettlementCurrency})
              </label>
              <input
                type="text"
                value={formatCurrency(calculatedValues.expectedDailyReturnSettlement, effectiveSettlementCurrency)}
                readOnly
                className={readOnlyInputClassName}
              />
            </div>
          )}
        <div>
          <label className={labelClassName}>
            Daily Earnings ({currencyCode})
          </label>
          <input
            type="text"
            value={formatCurrency(calculatedValues.expectedDailyReturnSecurity, currencyCode)}
            readOnly
            className={readOnlyInputClassName}
          />
        </div>
      </div>

      {/* Reason Comment — optional (feedback p.21). Still surfaced only for the
          reasons that benefit from a note (SPECIAL/CORPORATE_ACTION/OTHER), but
          no longer required: no asterisk, no validator, no submit gate. */}
      {REASONS_WITH_COMMENT.includes(form.state.values.trade_reason as TradeReasonValue) && (
        <div className="mt-4">
          <form.Field name="trade_reason_comment">
            {(field) => (
              <>
                <label className={labelClassName}>
                  Reason Comment
                </label>
                <textarea
                  rows={3}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'trade_reason_comment')}
                  placeholder={
                    form.state.values.trade_reason === 'SPECIAL'
                      ? 'Explain why this is a special trade (e.g., high demand, client request)...'
                      : form.state.values.trade_reason === 'CORPORATE_ACTION'
                      ? 'Describe the corporate action (e.g., dividend payment, stock split)...'
                      : 'Provide details about this trade...'
                  }
                  className={clsx(
                    inputClassName,
                    'resize-none',
                    inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                  )}
                />
              </>
            )}
          </form.Field>
        </div>
      )}
    </div>
  );
};
